import { useFlagMaxLifetimeEnforcementEnabled } from "@/hooks/use-flag";
import { MaximumEnvironmentLifetimeExceededError } from "@/utils/errors";
import { ConnectError } from "@connectrpc/connect";
import { PolicyEnforcedFailedPreconditionDetails } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useState } from "react";

/**
 * Encapsulates the max-lifetime enforcement flow for starting environments.
 *
 * Returns:
 * - `enforcementEnabled`: whether server-side enforcement is active
 * - `handleStartError`: call this in the catch block of startEnvironment;
 *    returns true if the error was handled (modal shown), false otherwise.
 * - `modalProps`: spread onto EnvironmentLifetimeExceededModal.
 */
export function useLifetimeEnforcement() {
    const { value: enforcementEnabled } = useFlagMaxLifetimeEnforcementEnabled();

    const [showModal, setShowModal] = useState(false);
    // Whether the modal was triggered by server-side enforcement or client-side check
    const [serverEnforced, setServerEnforced] = useState(false);

    const handleStartError = useCallback(
        (error: unknown): boolean => {
            // Client-side check (enforcement disabled)
            if (error instanceof MaximumEnvironmentLifetimeExceededError) {
                setServerEnforced(false);
                setShowModal(true);
                return true;
            }
            // Server-side enforcement error
            if (error instanceof ConnectError) {
                const details = error.findDetails(PolicyEnforcedFailedPreconditionDetails);
                const isLifetimeEnforcement = details.some(
                    (d) => d.policyName === "maximum_environment_lifetime",
                );
                if (isLifetimeEnforcement) {
                    setServerEnforced(true);
                    setShowModal(true);
                    return true;
                }
            }
            return false;
        },
        [],
    );

    return {
        enforcementEnabled,
        handleStartError,
        modalProps: {
            open: showModal,
            onClose: () => setShowModal(false),
            serverEnforced,
        },
    };
}
